import React, { Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import "@/i18n/config";

import { SettingsProvider } from "@/context/SettingsContext";
import { GuestAuthProvider } from "@/context/GuestAuthContext";
import { AdminAuthProvider, useAdminAuth } from "@/context/AdminAuthContext";

import AppShell from "@/layouts/AppShell";
import Home from "@/pages/Home";
import ApartmentDetail from "@/pages/ApartmentDetail";
import CategoryList from "@/pages/CategoryList";
import POIDetail from "@/pages/POIDetail";
import MapPage from "@/pages/MapPage";
import Contacts from "@/pages/Contacts";
import MeteoPage from "@/pages/MeteoPage";
import NotFound from "@/pages/NotFound";
import ErrorBoundary from "@/components/ErrorBoundary";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/Dashboard";
import PropertiesManager from "@/pages/admin/PropertiesManager";
import ApartmentsManager from "@/pages/admin/ApartmentsManager";
import CategoriesManager from "@/pages/admin/CategoriesManager";
import POIManager from "@/pages/admin/POIManager";
import SettingsManager from "@/pages/admin/SettingsManager";
import SecurityManager from "@/pages/admin/SecurityManager";
import TranslationsManager from "@/pages/admin/TranslationsManager";

function ProtectedAdmin({ children }) {
  const { token } = useAdminAuth();
  if (!token) return <Navigate to="/admin/login" replace />;
  return children;
}

function Providers({ children }) {
  return (
    <SettingsProvider>
      <GuestAuthProvider>
        <AdminAuthProvider>{children}</AdminAuthProvider>
      </GuestAuthProvider>
    </SettingsProvider>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Providers>
          <Toaster position="top-center" richColors />
          <Suspense fallback={null}>
            <Routes>
            {/* Guest routes */}
            <Route element={<AppShell><PageOutlet /></AppShell>}>
              <Route path="/" element={<Home />} />
              <Route path="/appartamento/:slug" element={<ApartmentDetail />} />
              <Route path="/categoria/meteo" element={<MeteoPage />} />
              <Route path="/categoria/:slug" element={<CategoryList />} />
              <Route path="/poi/:id" element={<POIDetail />} />
              <Route path="/mappa" element={<MapPage />} />
              <Route path="/contatti" element={<Contacts />} />
            </Route>

            {/* Admin routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<ProtectedAdmin><AdminLayout /></ProtectedAdmin>}>
              <Route index element={<AdminDashboard />} />
              <Route path="properties" element={<PropertiesManager />} />
              <Route path="apartments" element={<ApartmentsManager />} />
              <Route path="categories" element={<CategoriesManager />} />
              <Route path="pois" element={<POIManager />} />
              <Route path="settings" element={<SettingsManager />} />
              <Route path="security" element={<SecurityManager />} />
              <Route path="translations" element={<TranslationsManager />} />
            </Route>

            <Route path="*" element={<AppShell><NotFound /></AppShell>} />
          </Routes>
        </Suspense>
      </Providers>
    </BrowserRouter>
    </ErrorBoundary>
  );
}

// Wrapper to make AppShell work as layout with nested routes via <Outlet />.
function PageOutlet() { return <Outlet />; }
